import React, { useContext, useState } from 'react';
import {
  AppInsightsContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
} from '../azureAppInsights/AzureAppInsightsContext';
import { motion } from 'motion/react';
import MinusLight from '@/assets/minusLight.svg';
import PlusLight from '@/assets/plusLight.svg';
import { accordionPanel } from '@/animations';
import { MetadataContext, UserContext } from '../ContextProviders';
import { useRouter } from 'next/router';
import classNames from 'classnames';

type ReportModulHeaderModel = {
  title: string;
  expanded: boolean;
  eventKey?: TrackEventKey;
  className?: string;
  children?: React.ReactNode;
  saveOpenStateKey?: string;
};

const ReportModulHeader = ({
  title,
  expanded,
  eventKey,
  className,
  children,
  saveOpenStateKey,
}: ReportModulHeaderModel) => {
  const { asPath } = useRouter();
  const [isExpanded, setIsExpanded] = useState(() => {
    if (saveOpenStateKey && typeof window !== 'undefined') {
      const savedState = sessionStorage.getItem(`${saveOpenStateKey}-isExpanded:${asPath}`);
      if (savedState !== null) {
        return JSON.parse(savedState);
      }
    }
    return expanded;
  });
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId, isPublic } = useContext(MetadataContext);

  const toggle = () => {
    const newValue = !isExpanded;
    setIsExpanded(newValue);
    if (saveOpenStateKey) {
      sessionStorage.setItem(`${saveOpenStateKey}-isExpanded:${asPath}`, JSON.stringify(newValue));
    }
    if (eventKey) {
      appInsights.trackCustomEvent(
        TrackEventName.REPORT,
        eventKey,
        newValue ? TrackEventType.OPEN : TrackEventType.CLOSE,
        user,
        {
          pageId,
          pagePath: asPath,
        },
      );
    }
  };

  return (
    <div className={classNames(className, 'overflow-hidden')}>
      <button
        onClick={toggle}
        className={classNames(
          'title border-from-bg/20 flex w-full cursor-pointer items-center justify-between border-b py-4 text-lg leading-[22px] tracking-[0.16875px] lg:pt-0 lg:pb-5',
          !isPublic && 'font-bold',
        )}
      >
        {title}
        {isExpanded ? <MinusLight className="h-8 w-8 print:hidden" /> : <PlusLight className="h-8 w-8 print:hidden" />}
      </button>

      <motion.div
        variants={accordionPanel.variants}
        initial="inactive"
        animate={isExpanded ? 'active' : 'inactive'}
        className="print:!h-auto print:!opacity-100"
      >
        {children}
      </motion.div>
    </div>
  );
};

export default ReportModulHeader;
